import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

// Types for our slot system
interface SlotRegistry {
    [slotName: string]: ReactNode[];
}

interface SlotContextValue {
    slots: SlotRegistry;
    registerSlot: (name: string, content: ReactNode) => void;
    unregisterSlot: (name: string, content: ReactNode) => void;
}

// Create the context
const SlotContext = createContext<SlotContextValue | null>(null);

// Provider component
interface SlotProviderProps {
    children: ReactNode;
}

export const SlotProvider: React.FC<SlotProviderProps> = ({ children }) => {
    const [slots, setSlots] = useState<SlotRegistry>({});

    const registerSlot = useCallback((name: string, content: ReactNode) => {
        setSlots(prev => ({
            ...prev,
            [name]: [...(prev[name] || []), content],
        }));
    }, []);

    const unregisterSlot = useCallback((name: string, content: ReactNode) => {
        setSlots(prev => ({
            ...prev,
            [name]: (prev[name] || []).filter(item => item !== content),
        }));
    }, []);

    const value: SlotContextValue = {
        slots,
        registerSlot,
        unregisterSlot,
    };

    return <SlotContext.Provider value={value}>{children}</SlotContext.Provider>;
};

// Hook to use the slot context
const useSlotContext = () => {
    const context = useContext(SlotContext);
    if (!context) {
        throw new Error('useSlotContext must be used within a SlotProvider');
    }
    return context;
};

// Slot component - renders content for a specific slot
interface SlotProps {
    name: string;
    children?: (content: ReactNode[]) => ReactNode;
}

export const Slot: React.FC<SlotProps> = ({ name, children }) => {
    const { slots } = useSlotContext();
    const slotContent = slots[name] || [];

    if (children && typeof children === 'function') {
        return <>{children(slotContent)}</>;
    }

    return <>{slotContent}</>;
};

// Fill component - adds content to a specific slot
interface FillProps {
    name: string;
    children: ReactNode;
}

export const Fill: React.FC<FillProps> = ({ name, children }) => {
    const { registerSlot, unregisterSlot } = useSlotContext();

    React.useEffect(() => {
        registerSlot(name, children);

        return () => {
            unregisterSlot(name, children);
        };
    }, [name, children, registerSlot, unregisterSlot]);

    return null; // Fill components don't render anything directly
};
